import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import styled from "styled-components";
import { Grid, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { Create, Delete, Repeat } from "@material-ui/icons";
import moment from "moment";
import numberFormatter from "utils/numberFormatter";
import alertConfig from "constants/alertConfig";

import { NoItems } from "./NoItems";

const StyledButton = styled(Button)`
  && {
    margin-left: 4px;    
    border-radius: 0;
    color: #2196f3;
    background: none;
    position: unset;
    min-width:32px;
  }
  &&:hover {
    color:#43A8FF;
    background:none;
  }
`;

const OptionsCell = styled(TableCell)`
  &&& {
    text-align:right;
  }
  && div {
    display:flex;
    justify-content:flex-end;
  }
`;

const LB_BLUE = "#2196f3";

const styles = theme => ({
  root: {
    // width: 'calc(100% - 34px)',
    marginRight: "34px",
    overflowX: "auto"
  },
  table: {
    minWidth: 1000
  },
  forCell: {
    width: "15%",
    padding: 0,
    margin: 0,
    fontSize: "17px"
  },
  messageCell: {
    width: "30%",
    padding: 0,
    margin: 0,
    paddingLeft: "10px",
    fontSize: "17px"
  },
  triggerCell: {
    width: "25%",
    maxWidth: 200,
    padding: 0,
    margin: 0,
    paddingLeft: "10px",
    fontSize: "17px"
  },
  actionCell: {
    width: "5%",
    padding: 0,
    margin: 0,
    paddingLeft: "10px",
    textAlign: "right"
  }
});

const getAlertFor = item => {
  const NOT_SET = "Not Set";
  if (item.alertLevelId) {
    switch (item.alertLevelId) {
      case alertConfig.ALERT_FOR_DEAL:
        return item.deal ? `${item.deal.name}` : NOT_SET;
      case alertConfig.ALERT_FOR_FUND:
        return item.fund ? `${item.fund.name}` : NOT_SET;
      case alertConfig.ALERT_FOR_PORTFOLIO:
        return "Portfolio";
      case alertConfig.ALERT_FOR_GUARANTOR:
        return "Guarantor";
      default:
        return NOT_SET;
    }
  }

  return NOT_SET;
};

const countDecimals = value => (((value % 1) != 0) ? value.toString().split(".")[1].length : 0);

const getAlertValue = item => {
  const NOT_SET = "Not Set";
  if (item.alertType && item.alertType.alertFormatTypeId) {
    const accuracy = countDecimals(item.alertType.accuracy);

    switch (item.alertType.alertFormatTypeId) {
      case alertConfig.PERCENT:
        return item.triggerValue ? numberFormatter.formatRate(item.triggerValue / 100, accuracy) : NOT_SET;

      case alertConfig.CURRENCY:
        return item.triggerValue ? numberFormatter.formatMoney(item.triggerValue, accuracy) : NOT_SET;

      case alertConfig.NUMBER:
      case alertConfig.DATE:
        return item.triggerValue ? `${item.triggerValue}` : NOT_SET;

      default:
        return NOT_SET;
    }
  }

  return NOT_SET;
};

const getFormattedNotificationTriggerValue = alert => {
  switch (alert.alertType.alertFormatType.value) {
    case "PERCENT":
      return `${alert.testValue}%`;
    case "CURRENCY":
      return `${numberFormatter.formatMoney(alert.testValue)}`;
    case "NUMBER":
      return `${numberFormatter.formatDecimal(alert.testValue)}`;
    default:
      return alert.testValue;
  }
};

const sortAlerts = (alerts, isAlert) => {
  if (isAlert) {
    return alerts.sort((a, b) => moment.utc(b.updatedAt).unix() - moment.utc(a.updatedAt).unix());
  }

  return alerts.sort((a, b) => moment.utc(a.triggerValue).unix() - moment.utc(b.triggerValue).unix());
};

const findNextReminderDate = alert => {
  console.log("WORKING");
  const today = moment(new Date()).format("MM/DD/YYYY");
  const dates = [];

  if (alert.alertDefinitionValues && alert.alertDefinitionValues.length > 0) {
    for (let i = 0; i < alert.alertDefinitionValues.length; i++) {
      if (!alert.alertDefinitionValues[i].hasTriggered) {
        dates.push(alert.alertDefinitionValues[i].testValue);
      }
    }
  }

  const now = new Date();
  let closest = Infinity;

  dates.forEach(d => {
    const date = new Date(d);

    if (date >= now && (date < new Date(closest) || date < closest)) {
      closest = d;
    }
  });

  if (closest != Infinity) {
    return `${closest}`;
  }
};

const renderReminderTriggers = row => {
  if (row.alertDefinitionValues && row.alertDefinitionValues.length > 0) {
    return (
      <div key={row.id}>{`${findNextReminderDate(row)}`}</div>
    );
  }
};

const renderAlertTriggers = row => {
  const output = [];
  if (row.alertDefinitionValues && row.alertDefinitionValues.length > 0) {
    output.push(
      <div>
        <ul style={{ paddingLeft: "0px" }}>
          {row.alertDefinitionValues.map(currentValue => {
            { if (currentValue.id && currentValue.alertType && currentValue.alertType.alertName && currentValue.alertTestType && currentValue.alertTestType.value && (!currentValue.hasTriggered)) {
              return (
                <li key={currentValue.id}>{`${currentValue.alertType.alertName} ${currentValue.alertTestType.value} ${getFormattedNotificationTriggerValue(currentValue)}`}</li>
              );
            } }
          })}
        </ul>
      </div>
    );
  }
  if (output.length > 0) {
    return output;
  }
};
const AlertTable = props => {
  const { classes, editClick, deleteClick, alerts, isAlert, triggerLabel } = props;
  let bodyId = "";
  if (isAlert) {
    bodyId = "alertsTableBody";
  } else {
    bodyId = "remindersTableBody";
  }

  return (
    <Paper className={classes.root}>
      {alerts.length === 0 && (<NoItems Message={`No ${isAlert ? "alerts" : "reminders"} created`} />) }

      {alerts.length > 0 && (
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.forCell}>{`${isAlert ? "ALERT" : "REMINDER"} FOR`}</TableCell>
              <TableCell className={classes.messageCell}>MESSAGE</TableCell>
              <TableCell className={classes.triggerCell}>{triggerLabel}</TableCell>
              <TableCell className={classes.actionCell} />
            </TableRow>
          </TableHead>
          <TableBody id={bodyId}>
            {sortAlerts(alerts, isAlert).map(row => (
              <TableRow key={row.id}>
                <TableCell className={classes.forCell}>
                  {getAlertFor(row)}
                  {row.frequency && row.frequency != "oneTime" && <Tooltip title="Recurring"><div style={{ color: LB_BLUE, verticalAlign: "middle", display: "inline-block" }}><Repeat /></div></Tooltip>}
                </TableCell>
                <TableCell className={classes.messageCell}>{row.message && row.message.length > 65 ? `${row.message.substring(0, 65)}...` : row.message}</TableCell>
                <TableCell className={classes.triggerCell}>
                  {isAlert ?
                    row.alertDefinitionValues && row.alertDefinitionValues.length > 0 ?
                      renderAlertTriggers(row) : null :
                    null}

                  {!isAlert ?
                    row.alertDefinitionValues && row.alertDefinitionValues.length > 0 ?
                      renderReminderTriggers(row) : null :
                    null}
                </TableCell>
                <OptionsCell>
                  <div>
                    <StyledButton variant="contained" onClick={() => editClick(row)} disableRipple>
                      <Create />
                    </StyledButton>
                    <StyledButton variant="contained" onClick={() => deleteClick(row)} disableRipple>
                      <Delete />
                    </StyledButton>
                  </div>
                </OptionsCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) }
    </Paper>
  );
};

AlertTable.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(AlertTable);
